import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { convertTravelSportActivities, convertTravelSportActivitiesBack, getDefaultCoverageRange, i18n, showMessage, TravelPurposeEnum, TravelSportActivitiesEnum, useCreateSoWithCaptcha, useForm, useGeneralSoContext, useGetSo, useLoader, YesNoOptions } from "src/common";
import { isRateLimitDenial } from "src/common/apiGraphql/captcha";
import { CreateSoInput, GetSoDto, TravelDataType, TravelSportActivities } from "src/common/apiGraphql/types/types";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  Button,
  CaptchaChallenge,
  Card,
  ContainerComponent,
  Flex,
  Loader,
  PassengerDataForm,
  PassengerDataFormValues,
  Stepper,
  Text
} from "src/components";
import { colors } from "src/theme";
import { AppRoutes } from "./_router/app.routes";


const PassengerDataPage = () => {
  const { formRef, submitForm } = useForm();
  const navigate = useNavigate();
  const { so, loading, refetch } = useGetSo();
  const { soInitialData, setSoId, setSoInitialData, soId } = useGeneralSoContext();
  const { submit: createSo, captcha } = useCreateSoWithCaptcha({ page: 'passenger-data' });
  const [isBack, setIsBack] = useState<boolean>(false);
  const { startLoader, stopLoader } = useLoader();

  const { soId: soIdFromUrl } = useParams();

  useEffect(() => {
    if (soIdFromUrl) {
      setSoId(soIdFromUrl);
    }
  }, [soIdFromUrl]);

  useEffect(() => {
    if (so) {
      refetch()
    }
  }, []);

  useEffect(() => {
    if (so) {
      setSoInitialData(so.productTypeSpecific?.travelData as TravelDataType);
    }
  }, [so]);

  const handleFormSubmit = async (values: PassengerDataFormValues) => {
    startLoader();
    const data: CreateSoInput = formToModel(values);
    // A CAPTCHA_REQUIRED answer is handled by the hook: it renders the widget below the
    // form and resubmits this exact payload with the token, then runs these handlers.
    createSo(data, {
      onSuccess: (response) => {
        if (response.data?.createSo) {
          setSoId(response.data.createSo);
        }
        if (isBack) {
          navigate(AppRoutes.WEBSHOP.STEP_1);
        } else {
          navigate(AppRoutes.WEBSHOP.STEP_3);
        }
      },
      onError: (error) => {
        // RATE_LIMITED / IP_BLOCKED are already toasted by the Apollo error link.
        if (isRateLimitDenial(error)) return;
        showMessage({
          type: 'error',
          message: i18n.t('common.messages.error'),
          duration: 5000,
          fixed: true
        });
      },
      onSettled: () => stopLoader(),
    });
  };
  if (!soInitialData || (!so && soId)) {
    if (!soInitialData) {
      return <Navigate to={AppRoutes.WEBSHOP.STEP_1} />;
    }
    return null;
  }

  const getTravelPurpose = (so?: GetSoDto): TravelPurposeEnum => {
    if (so?.productTypeSpecific?.travelData?.sportActivities && so?.productTypeSpecific?.travelData?.sportActivities !== TravelSportActivities.None) {
      return TravelPurposeEnum.SPORT;
    }
    if (so?.productTypeSpecific?.travelData?.willWorkOnTrip) {
      return TravelPurposeEnum.WORK;
    }
    return TravelPurposeEnum.TURISTIC;
  }

  const modelToForm = (so?: GetSoDto): PassengerDataFormValues => ({
    travelPurpose: getTravelPurpose(so),
    willWorkOnTrip: so?.productTypeSpecific?.travelData?.willWorkOnTrip ? YesNoOptions()[0].value : YesNoOptions()[1].value,
    numOfInsuredPersons: so?.productTypeSpecific?.travelData?.numOfInsuredPersons || 1,
    numOfInsuredChildren: so?.productTypeSpecific?.travelData?.numOfInsuredChildren || 0,
    numOfInsuredAdults: (so?.productTypeSpecific?.travelData?.numOfInsuredPersons || 1) - (so?.productTypeSpecific?.travelData?.numOfInsuredChildren || 0),
    allInsuredPersonsAreFromTheSameFamily: so?.productTypeSpecific?.travelData?.allInsuredPersonsAreFromTheSameFamily || false,
    email: so?.email || '',
    luggageValue: so?.productTypeSpecific?.travelData?.luggageValue || undefined,
    tripValue: so?.productTypeSpecific?.travelData?.tripValue || undefined,
    sportActivityCode: so?.productTypeSpecific?.travelData?.sportActivityCode || undefined,
    sportActivities: convertTravelSportActivitiesBack(so?.productTypeSpecific?.travelData?.sportActivities) || TravelSportActivitiesEnum.AMATEUR
  });

  const getSportActivities = (travelPurpose: TravelPurposeEnum, sportActivites: TravelSportActivitiesEnum): TravelSportActivities => {
    if (travelPurpose !== TravelPurposeEnum.SPORT) {
      return TravelSportActivities.None
    } else {
      return convertTravelSportActivities(sportActivites);
    }
  }

  const formToModel = (values: PassengerDataFormValues): CreateSoInput => {
    return {
      email: values.email,
      travelData: {
        insuranceType: soInitialData.insuranceType,
        insuranceStartDate: soInitialData.insuranceStartDate,
        insuranceEndDate: soInitialData.insuranceEndDate,
        travelDestination: soInitialData.travelDestination,
        insuranceRange: so?.productTypeSpecific?.travelData?.insuranceRange || getDefaultCoverageRange(soInitialData.insuranceType),
        numOfInsuredAdults: values.numOfInsuredPersons - values.numOfInsuredChildren,
        numOfInsuredChildren: values.numOfInsuredChildren,
        sportActivities: getSportActivities(values.travelPurpose, values.sportActivities),
        willWorkOnTrip: values.willWorkOnTrip === YesNoOptions()[0].value ? true : false,
        numOfInsuredPersons: values.numOfInsuredPersons,
        allInsuredPersonsAreFromTheSameFamily: values.allInsuredPersonsAreFromTheSameFamily,
        longestTripDurationDays: soInitialData.longestTripDurationDays,
        longestTripDuration: soInitialData.longestTripDuration,
        luggageValue: values.luggageValue,
        tripValue: values.tripValue,
        sportActivityCode: values.sportActivityCode,
      },
      soId: so?._id || undefined,
      source: so?.source || undefined
    };
  };

  const passengerDataFormValues: PassengerDataFormValues = modelToForm(so as GetSoDto);

  const handleBack = async () => {
    if (so) {
      await setIsBack(true);
      await submitForm();
    } else {
      navigate(AppRoutes.WEBSHOP.STEP_1);
    }
  }

  return (
    <ContainerComponent>
      <Helmet>
        <title>Putnici | Putno osiguranje - kompare.hr</title>
      </Helmet>
      <Flex vertical gap={12}>
        <Card>
          <Flex vertical gap={32}>
            <Stepper currentStep={2} totalSteps={5} />
            <Flex vertical>
              <Text as="h1" type="xxLarge" bold>
                {i18n.t(`pages.passengerData.title`)}
              </Text>
            </Flex>
            <Flex vertical gap={40}>
              <PassengerDataForm ref={formRef} onSubmit={handleFormSubmit} passengerData={passengerDataFormValues} />
              {/* Appears only after the API asked for a captcha; solving it resubmits, so both buttons wait
                  (Back also submits createSo when an SO exists). */}
              <CaptchaChallenge {...captcha} />
              <Flex gap={24}>
                <Button iconPosition="left" btnType="textBlack" text={i18n.t("components.buttons.back")} onClick={handleBack} disabled={captcha.required}>
                  <ArrowLeftIcon color={colors.black} />
                </Button>
                <Button
                  btnType="primary"
                  text={i18n.t("pages.passengerData.showOffers")}
                  style={{ width: "100%" }}
                  onClick={() => submitForm()}
                  disabled={captcha.required}
                >
                  <ArrowRightIcon color={colors.white} />
                </Button>
              </Flex>
            </Flex>
            <Flex vertical gap={12}>
              <Text type="small" color={colors.text4} dangerouslySetInnerHTML={{ __html: i18n.t("pages.passengerData.gdprConsentText") }}>
              </Text>
            </Flex>
          </Flex>
        </Card>
        <Loader overlay={true} loading={loading} />
      </Flex>
    </ContainerComponent>
  );
};

export default PassengerDataPage;